import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Instagram,
  Linkedin,
  MapPin,
  Phone,
  Twitter,
  Youtube,
} from "lucide-react";
import { observer } from "mobx-react-lite";
import { useStore } from "../../app/stores/store";
import { all_routes } from "../router/all_routes";

// ================== Types ==================
interface SocialLink {
  Icon: React.ComponentType<{ size?: number }>;
  href: string;
}

interface PaymentLogo {
  src: string;
  alt: string;
  hint: string;
}

interface QuickLink {
  href: string;
  label: string;
}

interface LegalLink {
  href: string;
  label: string;
}

interface Category {
  id: number;
  name: string;
}

export const routes = all_routes;
// ================== Config ==================
const footerConfig = {
  quickLinks: [
    { href: routes.TrackOrder, label: "Track Visa" },
    { href: routes.login, label: "Login" },
  ] as QuickLink[],
  socials: [
    { Icon: Twitter, href: "#" },
    { Icon: Instagram, href: "#" },
    { Icon: Linkedin, href: "#" },
    { Icon: Youtube, href: "#" },
  ] as SocialLink[],
  paymentLogos: [
    { src: "/global/visa.webp", alt: "Visa", hint: "visa logo" },
    {
      src: "/global/mastercard.webp",
      alt: "Mastercard",
      hint: "mastercard logo",
    },
    { src: "/global/applepay.webp", alt: "Apple Pay", hint: "apple pay logo" },
    { src: "/global/paypal.webp", alt: "PayPal", hint: "paypal logo" },
  ] as PaymentLogo[],
  legal: [
    { href: routes.PrivacyPolicy, label: "Privacy Policy" },
    { href: routes.TermsConditions, label: "Terms and Conditions" },
  ] as LegalLink[],
};

// ================== Helper ==================
function formatName(name: string): string {
  return name.toLowerCase().replace(/\s+/g, "-");
}

// ================== Components ==================
const Logo: React.FC = () => (
  <div className="bg-white rounded-3xl py-2 px-4 flex items-center justify-center w-48">
    <img src="/logo.svg" alt="Uber E-Visa Logo" width={150} height={30} />
  </div>
);

// ================== Footer Component ==================
const Footer: React.FC = () => {
  const store = useStore();
  const categoryStore = store?.categoryStore;
  const categories = categoryStore?.categories ?? [];
  const loadCategories = categoryStore?.loadCategories;

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);
  const pages: QuickLink[] = [
    ...(categories && categories.length > 0
      ? categories.map((category) => ({
          href: `/services/${formatName(category.name)}/${category.id}`,
          label: category.name,
        }))
      : []),
    { href: "/who-we-are", label: "About Us" },
    { href: "/contact", label: "Contact Us" },
  ];

  return (
    <footer className="bg-background text-foreground py-8 w-full z-40">
      <div className="container mx-auto px-4">
        <div className="bg-neutral-900 text-neutral-300 rounded-2xl p-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            {/* Column 1: Logo + Socials */}
            <div className="md:col-span-4 flex flex-col items-start space-y-4">
              <Logo />
              <p className="text-neutral-400 text-sm">
                Your Fast-Track to Travel Freedom
              </p>
              <div className="flex space-x-3 pt-2">
                {footerConfig.socials.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    className="text-neutral-400 hover:text-white transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <social.Icon size={20} />
                  </a>
                ))}
              </div>
            </div>

            {/* Column 2: Pages + Quick Links */}
            <div className="md:col-span-4 grid grid-cols-2 gap-8">
              <div>
                <h3 className="font-semibold text-lg mb-4 text-white">Pages</h3>
                <ul className="space-y-2">
                  {pages.map((link) => (
                    <li key={link.label}>
                      <Link
                        to={link.href}
                        className="text-neutral-400 hover:text-white transition-colors"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-4 text-white">
                  Quick Links
                </h3>
                <ul className="space-y-2">
                  {footerConfig.quickLinks.map((link) => (
                    <li key={link.label}>
                      <Link
                        to={link.href}
                        className="text-neutral-400 hover:text-white transition-colors"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>

                <h3 className="font-semibold text-lg mb-4 text-white mt-6">
                  Location
                </h3>
                <ul className="space-y-2 text-neutral-400">
                  <li className="flex items-center gap-2">
                    <MapPin size={16} className="text-primary" />
                    <span>Al Karama, Dubai, UAE</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Phone size={16} className="text-primary" />
                    <a
                      href="tel:+97143426666"
                      className="hover:text-white transition-colors"
                    >
                      +971 4 342 6666
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            {/* Column 3: App + Payment */}
            <div className="md:col-span-4 flex flex-col items-start space-y-6">
              <div>
                <h3 className="font-semibold text-lg mb-4 text-white">
                  Get the App
                </h3>
                <div className="flex flex-row space-x-3">
                  <a href="#" className="hover:opacity-80 transition-opacity">
                    <img
                      src="/global/app-store.webp"
                      alt="Download on the App Store"
                      width={140}
                      height={48}
                      className="rounded-lg"
                    />
                  </a>
                  <a href="#" className="hover:opacity-80 transition-opacity">
                    <img
                      src="/global/google-play.webp"
                      alt="Get it on Google Play"
                      width={140}
                      height={48}
                      className="rounded-lg"
                    />
                  </a>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-lg mb-4 text-white">
                  We Accept
                </h3>
                <div className="flex items-center space-x-2">
                  {footerConfig.paymentLogos.map((logo) => (
                    <div key={logo.alt} className="bg-white rounded-md p-1">
                      <img
                        src={logo.src}
                        alt={logo.alt}
                        width={40}
                        height={26}
                        className="object-contain"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-neutral-700 mt-8 pt-6">
            <div className="flex flex-col sm:flex-row justify-between items-center text-sm text-neutral-400">
              <p>&copy; 2025 – Uber E-Visa. All Rights Reserved.</p>
              <div className="flex flex-col sm:flex-row items-center sm:space-x-4 mt-4 sm:mt-0 space-y-2 sm:space-y-0">
                {footerConfig.legal.map((link) => (
                  <Link
                    key={link.label}
                    to={link.href}
                    className="hover:text-white transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}
               
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default observer(Footer);
